/**
 * Pure logic behind the `/instagram-funnel` campaigns page (2026-09-24).
 *
 * Same house rule as `instagram-funnel-approval.ts`: anything worth being right
 * is worth being testable without a DOM. Everything here is a function of its
 * arguments — no fetching, no state, no side effects.
 *
 * Two of these are load-bearing rather than cosmetic:
 *
 *   - {@link buildCampaignPatch} decides what the Save button actually sends.
 *     The update action's patch has a null-vs-absent distinction — an absent
 *     key leaves the column alone, a key set to null CLEARS it — so a naive
 *     "send the whole form" would silently rewrite fields nobody touched, and
 *     a naive "send only truthy values" could never clear one.
 *
 *   - {@link campaignFormErrors} carries the SAME "needs a trigger" rule the
 *     action enforces, so the form says it in the field rather than after a
 *     round-trip. The action stays the enforcement (invariant 2: boundaries
 *     only); this is a courtesy, and is written to agree with it rather than
 *     to replace it.
 */

// Mirrored from `instagram-funnel-campaigns-shared.ts`, which mirrors migration
// 199's CHECK constraints. Pre-flight hints only — the chokepoint's JSON Schema
// and the column CHECKs are the enforcement.
export const CAMPAIGN_NAME_MAX = 120;
export const CAMPAIGN_TRIGGER_KEYWORD_MAX = 60;
export const CAMPAIGN_TRIGGER_MEDIA_ID_MAX = 64;
export const CAMPAIGN_DM_BODY_MAX = 900;
export const CAMPAIGN_CHIP_LABEL_MAX = 20;
export const CAMPAIGN_PAYLOAD_BODY_MAX = 900;
export const CAMPAIGN_PUBLIC_REPLY_MAX = 2200;
export const CAMPAIGN_COOLDOWN_DAYS_MAX = 3650;
export const CAMPAIGN_COOLDOWN_DAYS_DEFAULT = 90;

export type CampaignStatus = "draft" | "active" | "paused";
export type CampaignMode = "approve" | "auto";

export interface CampaignSendCounts {
  readonly staged: number;
  readonly sent: number;
  readonly skipped: number;
  readonly expired: number;
  readonly failed: number;
  readonly total: number;
}

export interface Campaign {
  readonly id: string;
  readonly name: string;
  readonly status: CampaignStatus;
  readonly trigger_keyword: string | null;
  readonly trigger_media_id: string | null;
  readonly dm_body: string;
  readonly chip_label: string;
  readonly payload_body: string;
  readonly payload_url: string | null;
  readonly public_reply_body: string | null;
  readonly cooldown_days: number;
  readonly mode: CampaignMode;
  readonly auto_public_reply: boolean;
  /** Migration 251 — the studio's one campaign copied onto every new post. */
  readonly covers_new_posts: boolean;
  /** Set on a copy the funnel made for a post with no campaign of its own; `null` = made by hand. */
  readonly copied_from_campaign_id: string | null;
  readonly created_at: string;
  readonly updated_at: string;
  readonly send_counts: CampaignSendCounts;
}

/**
 * The form's shape: text fields as strings, as the DOM hands them over, plus the
 * two controls that are not text — `mode` (a select, so a literal) and
 * `auto_public_reply` (a checkbox, so a boolean).
 */
export interface CampaignDraft {
  readonly name: string;
  readonly trigger_keyword: string;
  readonly trigger_media_id: string;
  readonly dm_body: string;
  readonly chip_label: string;
  readonly payload_body: string;
  readonly payload_url: string;
  readonly public_reply_body: string;
  readonly cooldown_days: string;
  readonly mode: CampaignMode;
  readonly auto_public_reply: boolean;
}

export function emptyCampaignDraft(): CampaignDraft {
  return {
    name: "",
    trigger_keyword: "",
    trigger_media_id: "",
    dm_body: "",
    chip_label: "",
    payload_body: "",
    payload_url: "",
    public_reply_body: "",
    cooldown_days: String(CAMPAIGN_COOLDOWN_DAYS_DEFAULT),
    // Both default to the QUIET side. A new campaign asks a human before it
    // sends, and does not post publicly — the same defaults the column DEFAULTs
    // carry, so the form and the schema cannot disagree about what "new" means.
    mode: "approve",
    auto_public_reply: false,
  };
}

export function draftFromCampaign(c: Campaign): CampaignDraft {
  return {
    name: c.name,
    trigger_keyword: c.trigger_keyword ?? "",
    trigger_media_id: c.trigger_media_id ?? "",
    dm_body: c.dm_body,
    chip_label: c.chip_label,
    payload_body: c.payload_body,
    payload_url: c.payload_url ?? "",
    public_reply_body: c.public_reply_body ?? "",
    cooldown_days: String(c.cooldown_days),
    mode: c.mode,
    auto_public_reply: c.auto_public_reply,
  };
}

/** `""` → null, so an emptied field CLEARS rather than writing an empty string. */
function orNull(v: string): string | null {
  const t = v.trim();
  return t.length === 0 ? null : t;
}

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

export type CampaignFieldErrors = Partial<Record<keyof CampaignDraft, string>>;

/**
 * Field-level problems, keyed by field. Empty object = nothing to say.
 *
 * `trigger_keyword` carries the no-trigger message because it is the field an
 * operator reaches for first; the page also renders it once at form level so
 * the rule is legible without hunting.
 *
 * `coversNewPosts` is the campaign being edited's own flag (migration 251): a
 * campaign used for every new post must keep BOTH triggers, and the form says
 * so in the field the operator is emptying rather than after a round-trip.
 */
export function campaignFormErrors(
  d: CampaignDraft,
  coversNewPosts = false,
): CampaignFieldErrors {
  const e: Record<string, string> = {};
  const name = d.name.trim();
  if (name.length === 0) e["name"] = "Give the campaign a name.";
  else if (name.length > CAMPAIGN_NAME_MAX)
    e["name"] = `${name.length} characters — the limit is ${CAMPAIGN_NAME_MAX}.`;

  const keyword = orNull(d.trigger_keyword);
  const mediaId = orNull(d.trigger_media_id);
  if (keyword === null && mediaId === null) {
    // THE RULE. A campaign matching on nothing matches everything.
    e["trigger_keyword"] =
      "Needs a keyword or a post — a campaign with neither would DM everyone who comments.";
  }
  if (keyword !== null && keyword.length > CAMPAIGN_TRIGGER_KEYWORD_MAX)
    e["trigger_keyword"] = `The limit is ${CAMPAIGN_TRIGGER_KEYWORD_MAX} characters.`;
  if (mediaId !== null && mediaId.length > CAMPAIGN_TRIGGER_MEDIA_ID_MAX)
    e["trigger_media_id"] = `The limit is ${CAMPAIGN_TRIGGER_MEDIA_ID_MAX} characters.`;

  const dm = d.dm_body.trim();
  if (dm.length === 0) e["dm_body"] = "The DM needs something to say.";
  else if (dm.length > CAMPAIGN_DM_BODY_MAX)
    e["dm_body"] = `${dm.length} characters — the limit is ${CAMPAIGN_DM_BODY_MAX}.`;

  const chip = d.chip_label.trim();
  if (chip.length === 0) e["chip_label"] = "The button needs a label.";
  else if (chip.length > CAMPAIGN_CHIP_LABEL_MAX)
    e["chip_label"] =
      `${chip.length} characters — Instagram truncates past ${CAMPAIGN_CHIP_LABEL_MAX}.`;

  const payload = d.payload_body.trim();
  if (payload.length === 0) e["payload_body"] = "Say what the tap delivers.";
  else if (payload.length > CAMPAIGN_PAYLOAD_BODY_MAX)
    e["payload_body"] =
      `${payload.length} characters — the limit is ${CAMPAIGN_PAYLOAD_BODY_MAX}.`;

  const url = orNull(d.payload_url);
  if (url !== null && !url.startsWith("https://"))
    e["payload_url"] = "Must start with https:// — a stranger is going to click it.";

  const reply = orNull(d.public_reply_body);
  if (reply !== null && reply.length > CAMPAIGN_PUBLIC_REPLY_MAX)
    e["public_reply_body"] = `The limit is ${CAMPAIGN_PUBLIC_REPLY_MAX} characters.`;

  const cooldown = Number(d.cooldown_days);
  if (!Number.isInteger(cooldown) || cooldown < 0 || cooldown > CAMPAIGN_COOLDOWN_DAYS_MAX)
    e["cooldown_days"] = `A whole number of days, 0 to ${CAMPAIGN_COOLDOWN_DAYS_MAX}.`;

  // THE AUTO GUARD, said in the field the operator must fix (2026-09-26). The
  // migration-201 CHECK and the actions both refuse this; the reason it is worth
  // ALSO saying here is that the failure it prevents is IRREVERSIBLE. A
  // keyword-only auto campaign cold-DMs anyone who writes that word anywhere on
  // the account, and a burned `instagram_funnel_sends` row can never be
  // re-staged, so that person is out of the funnel for good.
  if (d.mode === "auto" && mediaId === null) {
    e["trigger_media_id"] =
      "Auto mode needs one nominated post — without it the campaign would DM everyone who writes the keyword anywhere, autonomously, and a burned comment can never be funnelled again.";
  }

  // The other 201 CHECK: a toggle with nothing to post is a silent no-op, so it
  // is unrepresentable rather than tolerated.
  if (d.auto_public_reply && reply === null) {
    e["public_reply_body"] =
      "Write the public reply, or turn the toggle off — there is nothing to post.";
  }

  // Migration 251's CHECK, the update action's refusal 6. Copied onto every new
  // post, a campaign with no keyword would DM every commenter there.
  if (coversNewPosts) {
    const why =
      "This campaign is used for every new post, so it needs both a keyword and its own post — stop using it for new posts first.";
    if (keyword === null) e["trigger_keyword"] = why;
    if (mediaId === null) e["trigger_media_id"] = why;
  }

  return e as CampaignFieldErrors;
}

export function hasCampaignErrors(e: CampaignFieldErrors): boolean {
  return Object.keys(e).length > 0;
}

// ---------------------------------------------------------------------------
// Wire shapes
// ---------------------------------------------------------------------------

export interface CreateCampaignBody {
  readonly name: string;
  readonly trigger_keyword?: string | null;
  readonly trigger_media_id?: string | null;
  readonly dm_body: string;
  readonly chip_label: string;
  readonly payload_body: string;
  readonly payload_url?: string | null;
  readonly public_reply_body?: string | null;
  readonly cooldown_days: number;
  readonly mode?: CampaignMode;
  readonly auto_public_reply?: boolean;
}

/**
 * The create wire. No `status` — the action does not accept one (born draft).
 *
 * `mode` and `auto_public_reply` ARE sent, and both are sent unconditionally
 * rather than only when non-default: the action validates the pair together
 * (auto needs a post, an armed toggle needs copy), and a wire that omits one
 * half makes the guard decide against a value the operator never chose.
 */
export function buildCreateBody(d: CampaignDraft): CreateCampaignBody {
  return {
    name: d.name.trim(),
    mode: d.mode,
    auto_public_reply: d.auto_public_reply,
    ...(orNull(d.trigger_keyword) !== null
      ? { trigger_keyword: orNull(d.trigger_keyword) }
      : {}),
    ...(orNull(d.trigger_media_id) !== null
      ? { trigger_media_id: orNull(d.trigger_media_id) }
      : {}),
    dm_body: d.dm_body.trim(),
    chip_label: d.chip_label.trim(),
    payload_body: d.payload_body.trim(),
    ...(orNull(d.payload_url) !== null ? { payload_url: orNull(d.payload_url) } : {}),
    ...(orNull(d.public_reply_body) !== null
      ? { public_reply_body: orNull(d.public_reply_body) }
      : {}),
    cooldown_days: Number(d.cooldown_days),
  };
}

export type CampaignPatch = Partial<{
  name: string;
  trigger_keyword: string | null;
  trigger_media_id: string | null;
  dm_body: string;
  chip_label: string;
  payload_body: string;
  payload_url: string | null;
  public_reply_body: string | null;
  cooldown_days: number;
  mode: CampaignMode;
  auto_public_reply: boolean;
}>;

/**
 * The MINIMAL patch taking `before` to `draft` — only keys whose value really
 * changed.
 *
 * Why minimal matters, twice over. `fields_changed` in the audit row is
 * computed from the keys sent, so a full-form patch makes every save look like
 * a rewrite of everything. And an emptied optional field must arrive as an
 * explicit `null` to clear the column — dropping it would leave the old value
 * in place while the form shows it blank, which is the worst of both.
 */
export function buildCampaignPatch(before: Campaign, d: CampaignDraft): CampaignPatch {
  const p: Record<string, unknown> = {};
  const name = d.name.trim();
  if (name !== before.name) p["name"] = name;

  const keyword = orNull(d.trigger_keyword);
  if (keyword !== before.trigger_keyword) p["trigger_keyword"] = keyword;
  const mediaId = orNull(d.trigger_media_id);
  if (mediaId !== before.trigger_media_id) p["trigger_media_id"] = mediaId;

  const dm = d.dm_body.trim();
  if (dm !== before.dm_body) p["dm_body"] = dm;
  const chip = d.chip_label.trim();
  if (chip !== before.chip_label) p["chip_label"] = chip;
  const payload = d.payload_body.trim();
  if (payload !== before.payload_body) p["payload_body"] = payload;

  const url = orNull(d.payload_url);
  if (url !== before.payload_url) p["payload_url"] = url;
  const reply = orNull(d.public_reply_body);
  if (reply !== before.public_reply_body) p["public_reply_body"] = reply;

  const cooldown = Number(d.cooldown_days);
  if (Number.isInteger(cooldown) && cooldown !== before.cooldown_days)
    p["cooldown_days"] = cooldown;

  if (d.mode !== before.mode) p["mode"] = d.mode;
  // Compared, not coalesced. `false` is a MEANINGFUL value here — it disarms the
  // public reply — so the same `!== undefined` care the server-side fake repo
  // needed applies to the diff: `d.auto_public_reply || before` would make
  // turning the toggle off a no-op.
  if (d.auto_public_reply !== before.auto_public_reply)
    p["auto_public_reply"] = d.auto_public_reply;

  return p as CampaignPatch;
}

export function patchIsEmpty(p: CampaignPatch): boolean {
  return Object.keys(p).length === 0;
}

// ---------------------------------------------------------------------------
// Reading a campaign back
// ---------------------------------------------------------------------------

/**
 * The trigger, in words. Both set is an AND — that keyword, on that post — and
 * saying "and" out loud is the difference between a campaign that fires where
 * its author expected and one that does not.
 */
export function describeTrigger(c: Pick<Campaign, "trigger_keyword" | "trigger_media_id">): string {
  const k = c.trigger_keyword;
  const m = c.trigger_media_id;
  if (k !== null && m !== null) return `“${k}” — and only on one nominated post`;
  if (k !== null) return `“${k}” — on any post`;
  if (m !== null) return "any comment on one nominated post";
  // Unreachable through the actions (both refuse it) and through the column
  // CHECK. Said plainly rather than rendered as an empty string, because if it
  // ever appears it is the most important thing on the page.
  return "NO TRIGGER — this campaign would match every comment";
}

/**
 * What flipping this campaign to `active` will actually cause. Rendered in the
 * confirm step, because "Activate" on its own does not tell an operator that
 * the thing on the other side of the button is a message to a stranger.
 */
export function armingSummary(c: Campaign): ReadonlyArray<string> {
  const auto = c.mode === "auto";
  const lines = [
    `Comments matching ${describeTrigger(c)} will stage a DM.`,
    `Each person can be staged again only after ${c.cooldown_days} day${c.cooldown_days === 1 ? "" : "s"}.`,
  ];
  // The public reply line depends on the MODE, not only on the copy: a body with
  // the toggle off is still an approve-time reply, and saying "posted
  // automatically" for it would be wrong.
  lines.push(
    c.public_reply_body === null
      ? "No public comment reply — the DM only."
      : auto && c.auto_public_reply
        ? "A public reply is posted under the comment AUTOMATICALLY, with no human step."
        : "A public reply is posted under the comment when you approve.",
  );
  // The sentence the whole confirm step exists for. In `auto` it is the warning;
  // in `approve` it is the reassurance. Either way it goes LAST, because it is
  // the thing an operator must read before tapping Activate.
  lines.push(
    auto
      ? "⚠️ AUTONOMOUS — the DM is sent the moment a comment matches, with no human step. Meta allows one private reply per comment, so a send cannot be undone or repeated."
      : "Nothing is sent until you approve it in the inbox.",
  );
  return lines;
}

/** One line of outcome for the list row. */
export function describeSendCounts(counts: CampaignSendCounts): string {
  if (counts.total === 0) return "nothing staged yet";
  const parts: string[] = [];
  if (counts.sent > 0) parts.push(`${counts.sent} sent`);
  if (counts.staged > 0) parts.push(`${counts.staged} awaiting approval`);
  if (counts.skipped > 0) parts.push(`${counts.skipped} skipped`);
  if (counts.expired > 0) parts.push(`${counts.expired} expired`);
  if (counts.failed > 0) parts.push(`${counts.failed} failed`);
  return parts.join(" · ");
}

/**
 * Why this campaign cannot be deleted, or `null` when it can.
 *
 * The page asks BEFORE offering the button, so the destructive control is
 * absent rather than present-and-refusing. The action refuses regardless —
 * this is the courtesy, not the guard.
 */
export function deleteBlockedReason(c: Campaign): string | null {
  if (c.send_counts.total === 0) return null;
  return `${c.send_counts.total} send${c.send_counts.total === 1 ? "" : "s"} recorded — deleting would take the cooldown ledger with it, and everyone already messaged would be messaged again. Pause it instead.`;
}

/**
 * `mode` is LIVE since 2026-09-26 — `instagram_funnel_router`'s `auto_send` step
 * delivers an `auto` campaign's DM with no human step. Until then it was stored
 * and inert, and this function said so; it is the operator-facing half of that
 * change, so it moves in the same commit.
 */
export function describeMode(
  mode: CampaignMode,
  autoPublicReply = false,
): string {
  if (mode !== "auto") return "approve — a human taps before anything sends";
  return autoPublicReply
    ? "auto — sends the DM and posts the public reply, with no human step"
    : "auto — sends the DM with no human step";
}

/**
 * Why this campaign cannot be switched to `auto` yet, or `null` when it can.
 *
 * Mirrors {@link campaignFormErrors}' auto guard so the page can DISABLE the
 * control with a reason rather than offer one that will be refused. Third of the
 * three places this rule lives — the migration CHECK, the action's typed
 * refusal, and here — which is deliberate for an irreversible failure.
 */
export function autoModeBlockedReason(d: CampaignDraft): string | null {
  const mediaId = d.trigger_media_id.trim();
  if (mediaId.length > 0) return null;
  return "Nominate one post first — auto mode is scoped to a single post, never a keyword across the whole account.";
}

// ---------------------------------------------------------------------------
// "Every new post" (migration 251, 2026-10-24)
// ---------------------------------------------------------------------------

/**
 * Whether a card offers "Use for every new post". Mirrors the update action's
 * refusals so the button is ABSENT rather than present-and-refused: the
 * campaign must be live (handed to a draft or paused campaign, the coverage
 * would leave new posts unanswered) and carry a keyword AND a post of its own
 * (copied with no keyword, it would DM every commenter on every new post).
 */
export function canCoverNewPosts(c: Campaign): boolean {
  return (
    !c.covers_new_posts &&
    c.status === "active" &&
    c.trigger_keyword !== null &&
    c.trigger_media_id !== null
  );
}

/** The studio's covering campaign, or `null`. Migration 251 allows at most one. */
export function coveringCampaign(cs: ReadonlyArray<Campaign>): Campaign | null {
  return cs.find((c) => c.covers_new_posts) ?? null;
}

/**
 * What "Use for every new post" will do, for its confirm step — the job
 * {@link armingSummary} does for Activate, because this too widens who gets
 * messaged. `current` is the campaign it takes over from, if any: the tick
 * MOVES, and an operator should read that before it does.
 */
export function coverNewPostsSummary(
  c: Campaign,
  current: Campaign | null,
): ReadonlyArray<string> {
  const lines = [
    `When someone comments “${c.trigger_keyword ?? ""}” on a post that has no campaign of its own — a new post, or an older one nobody set up — this campaign is copied onto that post and answers them.`,
    "Each copy shows on this page as its own campaign — edit or pause it like any other.",
    // Said here because the rule is "no campaign of its own", not "new": an
    // older post is covered too, and deleting a post's campaign re-opens it.
    "To keep a post out, give it a campaign of its own — a paused one is enough. A post whose own campaign is deleted is covered again.",
  ];
  if (current !== null && current.id !== c.id) {
    lines.push(
      `“${current.name}” stops being used for new posts. The copies it already made stay as they are.`,
    );
  }
  lines.push(
    c.mode === "auto"
      ? "⚠️ AUTONOMOUS — like this campaign, every copy sends the DM the moment a comment matches, with no human step."
      : "Like this campaign, every copy waits for your approval in the inbox.",
  );
  return lines;
}

/**
 * The card's "New posts" line, or `null` when the campaign plays no part.
 *
 * Deleting the covering campaign NULLS its copies' link (migration 251's
 * `ON DELETE SET NULL`), so a copy whose source is gone arrives here as an
 * ordinary campaign and gets no line — it carries on answering its post.
 */
export function describeNewPostRole(
  c: Campaign,
  all: ReadonlyArray<Campaign>,
): string | null {
  if (c.covers_new_posts) {
    // A paused covering campaign keeps its tick but copies nothing — said out
    // loud, because "used for every post" alone would read as working.
    return c.status === "active"
      ? "used for every post without a campaign of its own, new or older"
      : `used for every post without a campaign of its own — but ${c.status}, so those posts are not being answered`;
  }
  if (c.copied_from_campaign_id !== null) {
    const source = all.find((x) => x.id === c.copied_from_campaign_id);
    // "onto this post", never "for a new post": the copied post may be an
    // older one nobody set up. Defensive only: the list is the studio's whole
    // set and the foreign key nulls a link to a deleted campaign, so the
    // source is always present.
    return source === undefined
      ? "copied onto this post"
      : `copied onto this post from “${source.name}”`;
  }
  return null;
}
